import { Component, inject, LOCALE_ID, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subscription } from 'rxjs'

import { ConfirmationService, MessageService } from 'primeng/api'
import { ButtonDirective } from 'primeng/button'
import { Drawer } from 'primeng/drawer'
import { DialogService } from 'primeng/dynamicdialog'

import { Toolbar } from 'primeng/toolbar'
import * as Sentry from '@sentry/angular'
import { PagesLoadedEvent, PdfThumbnailDrawnEvent, NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer'

import { ArchiveDocumentState } from '@app/constants/constants'
import { ArchiveDocumentForm, ArchiveDocumentSplitPayload } from '@app/interfaces/archive-documents'
import { ArchiveDocumentsApi } from '@app/apis/archive-documents.api'
import { DocumentService } from '@app/services/document.service'
import { Signal, SignalService } from '@app/services/signal.service'
import { TitleService } from '@app/services/title.service'
import { DialogSplitComponent } from '@app/components/viewer/dialog-split/dialog-split.component'
import { MetadataComponent } from '../metadata/metadata.component'

@Component({
  selector: 'app-viewer',
  templateUrl: 'viewer.component.html',
  styleUrl: 'viewer.component.less',
  encapsulation: ViewEncapsulation.None,
  imports: [
    Toolbar,
    NgxExtendedPdfViewerModule,
    ButtonDirective,
    Drawer,
    MetadataComponent,
  ],
})
export class ViewerComponent implements OnInit, OnDestroy {
  protected readonly signalService = inject(SignalService)
  protected readonly activatedRoute = inject(ActivatedRoute)
  protected readonly router = inject(Router)
  protected readonly dialogService = inject(DialogService)
  protected readonly confirmationService = inject(ConfirmationService)
  protected readonly messageService = inject(MessageService)
  protected readonly archiveDocumentsApi = inject(ArchiveDocumentsApi)
  protected readonly documentService = inject(DocumentService)
  protected readonly titleService = inject(TitleService)
  protected readonly locale = inject(LOCALE_ID)

  public ArchiveDocumentState = ArchiveDocumentState

  public document?: ArchiveDocumentForm
  private code?: string

  public documentSubscription?: Subscription
  public routeSubscription?: Subscription
  public signalServiceSubscription?: Subscription

  // the variable containing the PDF data
  public thePdfContent?: Blob
  // The pdf viewer will iterate over the whole document setting this var.
  // The last page is useful to us because we want to hide the cut/split icon
  public lastThumbnail?: HTMLElement

  public prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches

  // Control the visibility of the metadata pane show button
  public metadataVisible = true
  // Control the mask over the entire viewer pane (pdf and metadata), in order to drive
  //   user attention to the drafts panel during bulk update operations
  public showViewerMask = false

  ngOnInit (): void {
    // listen for route changes to load the new document/pdf
    this.routeSubscription = this.activatedRoute.params.subscribe(routeParams => {
      this.code = routeParams.code
      if (this.code) {
        setTimeout(() => {
          this.loadDoc(this.code!)
        })
      }
    })

    // listen for the editable document form to change for access to metadata
    this.documentSubscription = this.documentService.getDocument().subscribe((document?) => {
      if (document) {
        setTimeout(() => {
          this.document = document
          this.titleService.setPageTitle(this.document.filename)
        })
      }
    })

    // subscribe to the signal that will be used to mask the viewer area to focus attn on the sidebar
    this.signalServiceSubscription = this.signalService.getSignal().subscribe((signal: string) => {
      if (signal === Signal.SIGNAL_SHOW_MASK_VIEWER_PANE) {
        this.showViewerMask = true
      } else if (signal === Signal.SIGNAL_HIDE_MASK_VIEWER_PANE) {
        this.showViewerMask = false
      }
    })
  }

  ngOnDestroy (): void {
    this.routeSubscription?.unsubscribe()
    this.documentSubscription?.unsubscribe()
    this.signalServiceSubscription?.unsubscribe()
  }

  /**
   * load a new document into the viewer and metadata panes
   */
  loadDoc (uuid: string) {
    // load the pdf content
    this.archiveDocumentsApi.download(uuid).subscribe({
      next: (res) => {
        this.thePdfContent = res
      },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: '', detail: $localize`:@@viewer-error_downloading:Error downloading PDF` })
        throw error
      },
    })
  }

  /**
   * pdf.js could not open or parse the PDF (e.g. a corrupted page tree): warn the user and report it
   *   to Sentry as a warning tagged with the document, instead of letting it reach the global error handler
   */
  onPdfLoadingFailed (error: Error) {
    this.messageService.add({ severity: 'error', summary: '', detail: $localize`:@@viewer-error_rendering:This PDF is damaged and cannot be displayed correctly` })
    Sentry.captureException(error, { level: 'warning', tags: { document: this.code } })
  }

  /**
   * After displaying a confirmation, call the backend to split this PDF at the given page and refresh the drafts list
   */
  public onDoSplitClick ($event: Event) {
    const splitAfterPageNumber = parseInt(($event.target as HTMLInputElement).getAttribute('data-page-number') ?? '0')
    if (!splitAfterPageNumber) return

    const payload: ArchiveDocumentSplitPayload = {
      // propose a default new name for the new document extraction
      newName: `split-${this.document?.filename ?? 'document'}`,
      splitAfterPageNumber: splitAfterPageNumber,
    }
    // open the dialog to name / configure the new document
    const ref = this.dialogService.open(DialogSplitComponent, {
      header: $localize`:@@viewer-splitter-split-document:Split PDF document`,
      width: '30rem',
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      closable: true,
      focusOnShow: true,
      data: payload,
    })

    // listen for the dialog to close
    ref?.onClose.subscribe((splitPayload: ArchiveDocumentSplitPayload) => {
      // no document name is treated like a cancel
      if (!splitPayload?.newName || !this.document) return

      this.archiveDocumentsApi.split(this.document, splitPayload).subscribe(() => {
        this.signalService.sendSignal(Signal.SIGNAL_REFRESH_DRAFTS)
        this.loadDoc(this.document!.uuid)
      })
    })
  }

  /**
   * Delete an individual page in a PDF
   *
   */
  public onDeletePage ($event: Event) {
    const pageToDelete = parseInt(($event.target as HTMLInputElement).getAttribute('data-page-number') ?? '')
    if (!pageToDelete) return
    if (!this.document) return

    this.confirmationService.confirm({
      message: $localize`:@@viewer-delete_confirm:Are you sure that you want to delete page ${pageToDelete} ?`,
      accept: () => {
        if (!this.document) return
        this.archiveDocumentsApi.deletePage(this.document, pageToDelete).subscribe(() => {
          if (this.document) this.loadDoc(this.document.uuid)
        })
      },
    })
  }

  /**
   * all pages are loaded, hide the cut div for the last one
   */
  doPagesLoaded ($event: PagesLoadedEvent) {
    if (this.lastThumbnail) this.lastThumbnail.style.display = 'none'
    void this.checkPagesReadable($event)
  }

  /**
   * pdf.js only logs the pages it cannot load and leaves them blank: load every page so a damaged PDF is reported
   */
  async checkPagesReadable ($event: PagesLoadedEvent) {
    const pdfDocument = $event.source?.pdfDocument
    if (!pdfDocument) return

    const pages = await Promise.allSettled(Array.from({ length: $event.pagesCount }, (_, index) => pdfDocument.getPage(index + 1)))
    const failure = pages.find((page): page is PromiseRejectedResult => page.status === 'rejected')
    if (failure) this.onPdfLoadingFailed(failure.reason)
  }

  /**
   * this is called in order for each PDF page, used to get a handle on the last page / thumbnail
   */
  onThumbnailDrawn (thumbnailEvent: PdfThumbnailDrawnEvent) {
    this.lastThumbnail = thumbnailEvent.thumbnail.querySelector('.cut-div') as HTMLElement
  }
}
